import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { slidesData, moduleInfo } from "@/data/slidesData";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Grid3X3,
  Maximize,
  Minimize,
  Clock,
  Play,
  Pause,
  RotateCcw,
  CheckCircle2,
  XCircle,
  Coffee,
  UtensilsCrossed,
  ArrowRight,
  Users,
  MessageSquare,
  Lightbulb,
  BookOpen,
  Target,
  Shield,
  Brain,
  Layers,
  Sparkles,
  GraduationCap,
  Building2,
  Flag,
  Cog,
  Rocket,
  Snowflake,
  AlertTriangle,
  X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAHA_LOGO = "https://customer-assets.emergentagent.com/job_steadfast-ai-learn/artifacts/th8hp0xb_Seal_of_Maharashtra.png";
const STEADFAST_LOGO = "https://customer-assets.emergentagent.com/job_steadfast-ai-learn/artifacts/e4z23ods_logo.png";

const iconMap = {
  flag: Flag, brain: Brain, cog: Cog, layers: Layers, coffee: Coffee,
  sparkles: Sparkles, utensils: UtensilsCrossed, users: Users,
  building: Building2, 'graduation-cap': GraduationCap, shield: Shield,
  lightbulb: Lightbulb, 'message-square': MessageSquare, snowflake: Snowflake,
  rocket: Rocket, check: CheckCircle2, alert: AlertTriangle, x: X,
  target: Target, book: BookOpen,
};

const LogoBar = () => (
  <div className="logo-bar" data-testid="logo-bar">
    <img src={STEADFAST_LOGO} alt="Steadfast" className="logo-img" data-testid="steadfast-logo" />
    <img src={MAHA_LOGO} alt="Maharashtra" className="logo-img maha-logo" data-testid="maharashtra-logo" />
  </div>
);

const SlideNumber = ({ current, total }) => (
  <div className="slide-number" data-testid="slide-number">{current} / {total}</div>
);

// ================ SLIDE TYPE RENDERERS ================

const TitleSlide = ({ slide }) => (
  <div className="slide-content title-slide" data-testid="title-slide">
    <LogoBar />
    <div className="title-center">
      <h1 className="main-title" data-testid="slide-title">{slide.title}</h1>
      {slide.marathi && <p className="marathi-subtitle">{slide.marathi}</p>}
      <div className="title-divider" />
      {slide.subtitle && <p className="title-subtitle">{slide.subtitle}</p>}
      {slide.event && <span className="title-badge">{slide.event}</span>}
      {slide.audience && <p className="title-audience">{slide.audience}</p>}
    </div>
  </div>
);

const SectionSlide = ({ slide }) => (
  <div className="slide-content section-slide" data-testid="section-slide">
    <div className="section-logo-bar">
      <img src={STEADFAST_LOGO} alt="Steadfast" className="section-logo" data-testid="steadfast-logo" />
      <img src={MAHA_LOGO} alt="Maharashtra" className="section-logo maha-section-logo" data-testid="maharashtra-logo" />
    </div>
    <div className="section-inner">
      <span className="section-module-tag">{slide.moduleNumber}</span>
      <h1 className="section-title" data-testid="slide-title">{slide.title}</h1>
      {slide.marathi && <p className="section-marathi">{slide.marathi}</p>}
      <div className="section-objectives">
        <p className="obj-label">In this section, we will learn:</p>
        {slide.learningObjectives?.map((obj, i) => (
          <div key={i} className="obj-item"><CheckCircle2 size={18} /> <span>{obj}</span></div>
        ))}
      </div>
    </div>
  </div>
);

const ContentSlide = ({ slide }) => (
  <div className="slide-content content-slide" data-testid="content-slide">
    <LogoBar />
    <div className="content-header">
      <h2 className="content-title" data-testid="slide-title">{slide.title}</h2>
      {slide.marathi && <p className="content-marathi">{slide.marathi}</p>}
    </div>
    <div className="content-body">
      {slide.content && <p className="content-text">{slide.content}</p>}
      {slide.definition && <div className="definition-box">{slide.definition}</div>}
      {slide.bullets && (
        <ul className="content-bullets">
          {slide.bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      )}
      {slide.agenda && (
        <div className="agenda-grid">
          {slide.agenda.map((item, i) => {
            const Icon = iconMap[item.icon] || Clock;
            return (
              <div key={i} className={`agenda-item ${item.topic.includes('Break') ? 'break-item' : ''}`}>
                <Icon size={16} />
                <span className="agenda-time">{item.time}</span>
                <span className="agenda-topic">{item.topic}</span>
              </div>
            );
          })}
        </div>
      )}
      {slide.comparison && <ComparisonColumns left={slide.comparison.left} right={slide.comparison.right} />}
      {slide.cases && (
        <div className="cases-grid">
          {slide.cases.map((c, i) => (
            <div key={i} className={`case-card case-${c.color}`}>
              <span className="case-label">{c.label}</span>
              <p className="case-text">{c.text}</p>
              <p className="case-result">{c.result}</p>
            </div>
          ))}
        </div>
      )}
      {slide.timeline && (
        <div className="timeline">
          {slide.timeline.map((t, i) => {
            const Icon = iconMap[t.icon] || Clock;
            return (
              <div key={i} className="timeline-item">
                <div className="timeline-dot"><Icon size={16} /></div>
                <div className="timeline-content">
                  <span className="timeline-year">{t.year}</span>
                  <p className="timeline-event">{t.event}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {slide.tableData && (
        <div className="slide-table-wrap">
          <table className="slide-table">
            <thead>
              <tr>{slide.tableData.headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {slide.tableData.rows.map((row, i) => (
                <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {slide.tools && (
        <div className="tools-grid">
          {slide.tools.map((t, i) => (
            <div key={i} className="tool-card">
              <span className="tool-name">{t.name}</span>
              <span className="tool-desc">{t.desc}</span>
            </div>
          ))}
        </div>
      )}
      {slide.framework && (
        <div className="framework-list">
          {slide.framework.map((f, i) => (
            <div key={i} className="framework-item">
              <span className="fw-letter">{f.letter}</span>
              <div className="fw-content">
                <span className="fw-word">{f.word} ({f.marathi})</span>
                <span className="fw-desc">{f.desc}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {slide.challenges && (
        <div className="challenges-list">
          {slide.challenges.map((c, i) => (
            <div key={i} className="challenge-card">
              <h4 className="challenge-title">{c.title}</h4>
              <p className="challenge-desc">{c.description}</p>
              <p className="challenge-fix">{c.solution}</p>
            </div>
          ))}
        </div>
      )}
      {slide.translationExamples && (
        <div className="translation-examples">
          {slide.translationExamples.map((ex, i) => (
            <div key={i} className="trans-card">
              <h4>{ex.title}</h4>
              <p className="trans-prompt">"{ex.prompt}"</p>
              <p className="trans-tip">{ex.tip}</p>
            </div>
          ))}
        </div>
      )}
      {slide.actionPlan && (
        <div className="action-plan-grid">
          {slide.actionPlan.map((a, i) => (
            <div key={i} className="action-field">
              <span className="action-label">{a.field}:</span>
              <span className="action-placeholder">{a.placeholder}</span>
            </div>
          ))}
        </div>
      )}
      {slide.warning && <div className="warning-box"><AlertTriangle size={18} /> {slide.warning}</div>}
      {slide.impact && (
        <div className="impact-box">
          <h4>Impact:</h4>
          {slide.impact.map((imp, i) => <p key={i}><ArrowRight size={14} /> {imp}</p>)}
        </div>
      )}
    </div>
    {slide.keyTakeaway && (
      <div className="key-takeaway">
        <Lightbulb size={16} />
        <span>{slide.keyTakeaway}</span>
      </div>
    )}
  </div>
);

const ComparisonColumns = ({ left, right }) => (
  <div className="comparison-grid">
    <div className="comp-col comp-left">
      <h4>{left.title}</h4>
      <ul>{left.items.map((item, i) => <li key={i}>{item}</li>)}</ul>
    </div>
    <div className="comp-col comp-right">
      <h4>{right.title}</h4>
      <ul>{right.items.map((item, i) => <li key={i}>{item}</li>)}</ul>
    </div>
  </div>
);

const QuizSlide = ({ slide, quizState, onAnswer }) => {
  const renderStandardQuiz = () => (
    <div className="quiz-questions">
      {slide.questions?.map((q, qi) => (
        <div key={qi} className="quiz-question-block">
          <p className="quiz-q">{q.question}</p>
          <div className="quiz-options">
            {q.options.map((opt, oi) => {
              const answered = quizState?.[qi] !== undefined;
              const selected = quizState?.[qi] === oi;
              const isCorrect = oi === q.correct;
              let cls = "quiz-option";
              if (answered) {
                if (isCorrect) cls += " correct";
                else if (selected) cls += " incorrect";
              }
              return (
                <button
                  key={oi}
                  className={cls}
                  onClick={() => !answered && onAnswer(qi, oi)}
                  disabled={answered}
                  data-testid={`quiz-option-${qi}-${oi}`}
                >
                  <span className="opt-label">{String.fromCharCode(65 + oi)}</span>
                  <span>{opt}</span>
                  {answered && isCorrect && <CheckCircle2 size={16} className="opt-icon correct-icon" />}
                  {answered && selected && !isCorrect && <XCircle size={16} className="opt-icon incorrect-icon" />}
                </button>
              );
            })}
          </div>
          {quizState?.[qi] !== undefined && (
            <div className={`quiz-explanation ${quizState[qi] === q.correct ? 'exp-correct' : 'exp-incorrect'}`}>
              {q.explanation}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderTrueFalse = () => (
    <div className="quiz-questions">
      {slide.trueFalse?.map((q, qi) => {
        const answered = quizState?.[qi] !== undefined;
        const userAnswer = quizState?.[qi];
        return (
          <div key={qi} className="quiz-question-block tf-block">
            <p className="quiz-q">"{q.statement}"</p>
            <div className="tf-options">
              {[true, false].map((val) => {
                let cls = "tf-btn";
                if (answered) {
                  if (val === q.answer) cls += " correct";
                  else if (val === userAnswer) cls += " incorrect";
                }
                return (
                  <button
                    key={String(val)}
                    className={cls}
                    onClick={() => !answered && onAnswer(qi, val)}
                    disabled={answered}
                    data-testid={`tf-option-${qi}-${val}`}
                  >
                    {val ? "TRUE - AI" : "FALSE - Not AI"}
                  </button>
                );
              })}
            </div>
            {answered && <div className={`quiz-explanation ${userAnswer === q.answer ? 'exp-correct' : 'exp-incorrect'}`}>{q.explanation}</div>}
          </div>
        );
      })}
    </div>
  );

  const renderFillBlanks = () => (
    <div className="quiz-questions fill-blanks">
      {slide.fillBlanks?.map((q, qi) => {
        const answered = quizState?.[qi] !== undefined;
        return (
          <div key={qi} className="fill-blank-item">
            <p className="quiz-q">{q.sentence}</p>
            {!answered ? (
              <div className="fill-hint">
                <Lightbulb size={14} /> Hint: {q.hint}
                <button className="reveal-btn" onClick={() => onAnswer(qi, q.answer)} data-testid={`reveal-btn-${qi}`}>
                  Reveal Answer
                </button>
              </div>
            ) : (
              <div className="fill-answer"><CheckCircle2 size={16} /> <strong>{q.answer}</strong></div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderPoll = () => (
    <div className="quiz-questions poll-questions">
      {slide.pollQuestions?.map((q, qi) => {
        const answered = quizState?.[qi] !== undefined;
        return (
          <div key={qi} className="poll-item">
            <p className="quiz-q">{q.question}</p>
            <div className="poll-options">
              {q.options.map((opt, oi) => (
                <button
                  key={oi}
                  className={`poll-btn ${quizState?.[qi] === oi ? 'selected' : ''}`}
                  onClick={() => onAnswer(qi, oi)}
                  data-testid={`poll-option-${qi}-${oi}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        );
      })}
      {slide.keyTakeaway && Object.keys(quizState || {}).length === slide.pollQuestions?.length && (
        <div className="key-takeaway"><Lightbulb size={16} /><span>{slide.keyTakeaway}</span></div>
      )}
    </div>
  );

  const renderMatch = () => (
    <div className="quiz-questions match-quiz">
      <p className="match-instruction">{slide.matchQuiz?.instruction}</p>
      <div className="match-columns">
        <div className="match-col">
          <h4>Column A</h4>
          {slide.matchQuiz?.columnA.map((item) => (
            <div key={item.id} className="match-item">{item.id}. {item.text}</div>
          ))}
        </div>
        <div className="match-col">
          <h4>Column B</h4>
          {slide.matchQuiz?.columnB.map((item) => (
            <div key={item.id} className="match-item">{item.id}. {item.text}</div>
          ))}
        </div>
      </div>
      {!quizState?.revealed ? (
        <button className="reveal-btn match-reveal" onClick={() => onAnswer('revealed', true)} data-testid="match-reveal-btn">
          Reveal Answers
        </button>
      ) : (
        <div className="match-answers">
          {Object.entries(slide.matchQuiz?.correctMatches || {}).map(([a, b]) => {
            const itemA = slide.matchQuiz.columnA.find(x => x.id === a);
            const itemB = slide.matchQuiz.columnB.find(x => x.id === b);
            return (
              <div key={a} className="match-answer-row">
                <span>{a}. {itemA?.text}</span>
                <ArrowRight size={16} />
                <span>{b}. {itemB?.text}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="slide-content quiz-slide" data-testid="quiz-slide">
      <LogoBar />
      <div className="content-header">
        <h2 className="content-title" data-testid="slide-title">{slide.title}</h2>
        {slide.marathi && <p className="content-marathi">{slide.marathi}</p>}
      </div>
      <div className="content-body">
        {slide.questions && renderStandardQuiz()}
        {slide.trueFalse && renderTrueFalse()}
        {slide.fillBlanks && renderFillBlanks()}
        {slide.pollQuestions && renderPoll()}
        {slide.matchQuiz && renderMatch()}
      </div>
    </div>
  );
};

const ActivitySlide = ({ slide }) => (
  <div className="slide-content activity-slide" data-testid="activity-slide">
    <LogoBar />
    <div className="content-header">
      <h2 className="content-title" data-testid="slide-title">{slide.title}</h2>
      {slide.marathi && <p className="content-marathi">{slide.marathi}</p>}
    </div>
    <div className="content-body">
      {slide.content && <p className="content-text">{slide.content}</p>}
      {slide.promptToEvaluate && <div className="prompt-evaluate-box">"{slide.promptToEvaluate}"</div>}
      {slide.promptTemplate && <div className="prompt-template-box">{slide.promptTemplate}</div>}
      {slide.instructions && (
        <div className="activity-instructions">
          {slide.instructions.map((inst, i) => (
            <div key={i} className="instruction-step">
              <span className="step-num">{i + 1}</span>
              <span>{inst}</span>
            </div>
          ))}
        </div>
      )}
      {slide.improvedVersion && (
        <div className="improved-version">
          <h4><CheckCircle2 size={16} /> Improved Version:</h4>
          <p>"{slide.improvedVersion}"</p>
        </div>
      )}
      {slide.feelings && (
        <div className="feelings-grid">
          {slide.feelings.map((f, i) => (
            <span key={i} className="feeling-tag">{f}</span>
          ))}
        </div>
      )}
      <div className="activity-meta">
        {slide.duration && <span className="meta-badge duration-badge"><Clock size={14} /> {slide.duration}</span>}
        {slide.format && <span className="meta-badge format-badge"><Users size={14} /> {slide.format}</span>}
      </div>
      {slide.facilitatorTip && (
        <div className="facilitator-tip">
          <MessageSquare size={14} />
          <span><strong>Facilitator Tip:</strong> {slide.facilitatorTip}</span>
        </div>
      )}
    </div>
    {slide.keyTakeaway && (
      <div className="key-takeaway"><Lightbulb size={16} /><span>{slide.keyTakeaway}</span></div>
    )}
  </div>
);

const TimerSlide = ({ slide, timerState, onToggle, onReset }) => {
  const totalSeconds = (slide.minutes || 15) * 60;
  const remaining = timerState?.remaining ?? totalSeconds;
  const isRunning = timerState?.isRunning ?? false;
  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const progress = ((totalSeconds - remaining) / totalSeconds) * 100;

  return (
    <div className="slide-content timer-slide" data-testid="timer-slide">
      <LogoBar />
      <div className="timer-center">
        <h2 className="timer-label" data-testid="slide-title">{slide.label || slide.title}</h2>
        <div className="timer-ring-wrap">
          <svg className="timer-ring" viewBox="0 0 200 200">
            <circle cx="100" cy="100" r="90" fill="none" stroke="#f0f0f0" strokeWidth="8" />
            <circle
              cx="100" cy="100" r="90" fill="none" stroke="#f1992a" strokeWidth="8"
              strokeDasharray={565.48} strokeDashoffset={565.48 * (1 - progress / 100)}
              strokeLinecap="round" transform="rotate(-90 100 100)"
              style={{ transition: 'stroke-dashoffset 1s linear' }}
            />
          </svg>
          <div className="timer-display" data-testid="timer-display">
            <span className="timer-mins">{String(mins).padStart(2, '0')}</span>
            <span className="timer-sep">:</span>
            <span className="timer-secs">{String(secs).padStart(2, '0')}</span>
          </div>
        </div>
        <div className="timer-controls">
          <button className="timer-btn" onClick={onToggle} data-testid="timer-toggle-btn">
            {isRunning ? <Pause size={20} /> : <Play size={20} />}
            {isRunning ? 'Pause' : 'Start'}
          </button>
          <button className="timer-btn reset-btn" onClick={onReset} data-testid="timer-reset-btn">
            <RotateCcw size={20} /> Reset
          </button>
        </div>
      </div>
    </div>
  );
};

const BreakSlide = ({ slide }) => (
  <div className="slide-content break-slide" data-testid="break-slide">
    <div className="break-logo-bar">
      <img src={STEADFAST_LOGO} alt="Steadfast" className="break-logo" data-testid="steadfast-logo" />
      <img src={MAHA_LOGO} alt="Maharashtra" className="break-logo maha-break-logo" data-testid="maharashtra-logo" />
    </div>
    <div className="break-center">
      <div className="break-icon">
        {slide.title.includes('Tea') ? <Coffee size={64} /> : <UtensilsCrossed size={64} />}
      </div>
      <h1 className="break-title" data-testid="slide-title">{slide.title}</h1>
      {slide.marathi && <p className="break-marathi">{slide.marathi}</p>}
      <span className="break-duration"><Clock size={18} /> {slide.duration}</span>
      <p className="break-message">{slide.message}</p>
      {slide.funFact && <div className="fun-fact"><Lightbulb size={16} /> {slide.funFact}</div>}
    </div>
  </div>
);

const ClosingSlide = ({ slide }) => (
  <div className="slide-content closing-slide" data-testid="closing-slide">
    <LogoBar />
    <div className="closing-center">
      <h1 className="closing-title" data-testid="slide-title">{slide.title}</h1>
      {slide.marathi && <p className="closing-marathi">{slide.marathi}</p>}
      <div className="closing-summary">
        <h3>Key Takeaways:</h3>
        {slide.summary?.map((s, i) => (
          <div key={i} className="summary-item"><CheckCircle2 size={16} /> <span>{s}</span></div>
        ))}
      </div>
      <div className="closing-homework">
        <h3>Your Homework:</h3>
        {slide.homework?.map((h, i) => (
          <div key={i} className="homework-item"><ArrowRight size={14} /> <span>{h}</span></div>
        ))}
      </div>
      <div className="closing-message">{slide.closingMessage}</div>
      {slide.marathiClosing && <p className="closing-marathi-msg">{slide.marathiClosing}</p>}
    </div>
  </div>
);

// ================ SIDEBAR ================

const Sidebar = ({ slides, current, onSelect, onClose, modules }) => (
  <div className="sidebar-overlay" onClick={onClose} data-testid="sidebar-overlay">
    <div className="sidebar" onClick={e => e.stopPropagation()} data-testid="sidebar">
      <div className="sidebar-header">
        <h3>Slide Overview</h3>
        <button onClick={onClose} className="sidebar-close" data-testid="sidebar-close"><X size={20} /></button>
      </div>
      <div className="sidebar-modules">
        {modules.map((mod, mi) => (
          <div key={mi} className="sidebar-module">
            <div className="sidebar-module-header">
              <span className="module-tag">{mod.title}</span>
              <span className="module-duration">{mod.duration}</span>
            </div>
            <div className="sidebar-slides">
              {slides.filter(s => {
                const parts = mod.slides.split('-');
                const start = parseInt(parts[0]);
                const end = parseInt(parts[1] || parts[0]);
                return s.id >= start && s.id <= end;
              }).map(s => (
                <button
                  key={s.id}
                  className={`sidebar-slide-btn ${s.id === current + 1 ? 'active' : ''}`}
                  onClick={() => { onSelect(s.id - 1); onClose(); }}
                  data-testid={`sidebar-slide-${s.id}`}
                >
                  <span className="ss-num">{s.id}</span>
                  <span className="ss-title">{s.title}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ================ MAIN APP ================

function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [quizStates, setQuizStates] = useState({});
  const [timerStates, setTimerStates] = useState({});
  const [showSidebar, setShowSidebar] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const timerRef = useRef(null);
  const slideRef = useRef(null);
  const slides = slidesData;
  const slide = slides[currentSlide];

  const goNext = useCallback(() => {
    if (currentSlide < slides.length - 1) setCurrentSlide(c => c + 1);
  }, [currentSlide, slides.length]);

  const goPrev = useCallback(() => {
    if (currentSlide > 0) setCurrentSlide(c => c - 1);
  }, [currentSlide]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); goNext(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      if (e.key === 'Escape' && showSidebar) setShowSidebar(false);
      if (e.key === 'f' || e.key === 'F') toggleFullscreen();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goNext, goPrev, showSidebar]);

  // Timer logic
  useEffect(() => {
    if (slide?.type === 'timer') {
      const ts = timerStates[slide.id];
      if (ts?.isRunning && ts.remaining > 0) {
        timerRef.current = setInterval(() => {
          setTimerStates(prev => {
            const cur = prev[slide.id];
            if (!cur || cur.remaining <= 1) {
              clearInterval(timerRef.current);
              return { ...prev, [slide.id]: { ...cur, remaining: 0, isRunning: false } };
            }
            return { ...prev, [slide.id]: { ...cur, remaining: cur.remaining - 1 } };
          });
        }, 1000);
      }
    }
    return () => clearInterval(timerRef.current);
  }, [slide, timerStates, currentSlide]);

  const handleQuizAnswer = (slideId, qIndex, answer) => {
    setQuizStates(prev => ({
      ...prev,
      [slideId]: { ...(prev[slideId] || {}), [qIndex]: answer },
    }));
  };

  const handleTimerToggle = () => {
    const totalSec = (slide.minutes || 15) * 60;
    setTimerStates(prev => {
      const cur = prev[slide.id] || { remaining: totalSec, isRunning: false };
      return { ...prev, [slide.id]: { ...cur, isRunning: !cur.isRunning } };
    });
  };

  const handleTimerReset = () => {
    clearInterval(timerRef.current);
    const totalSec = (slide.minutes || 15) * 60;
    setTimerStates(prev => ({
      ...prev,
      [slide.id]: { remaining: totalSec, isRunning: false },
    }));
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      slideRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/export-pptx`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'AI_Training_Maharashtra_70_Slides.pptx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Download failed. Please try again.');
    }
    setDownloading(false);
  };

  const renderSlide = () => {
    switch (slide.type) {
      case 'title': return <TitleSlide slide={slide} />;
      case 'section': return <SectionSlide slide={slide} />;
      case 'quiz': return (
        <QuizSlide
          slide={slide}
          quizState={quizStates[slide.id] || {}}
          onAnswer={(qi, ans) => handleQuizAnswer(slide.id, qi, ans)}
        />
      );
      case 'activity': return <ActivitySlide slide={slide} />;
      case 'timer': return (
        <TimerSlide
          slide={slide}
          timerState={timerStates[slide.id]}
          onToggle={handleTimerToggle}
          onReset={handleTimerReset}
        />
      );
      case 'break': return <BreakSlide slide={slide} />;
      case 'closing': return <ClosingSlide slide={slide} />;
      default: return <ContentSlide slide={slide} />;
    }
  };

  const progress = ((currentSlide + 1) / slides.length) * 100;

  return (
    <div className="presentation-app" ref={slideRef} data-testid="presentation-app">
      {/* Top Controls */}
      <div className="top-controls" data-testid="top-controls">
        <button className="ctrl-btn" onClick={() => setShowSidebar(true)} data-testid="overview-btn" title="Slide Overview">
          <Grid3X3 size={18} />
        </button>
        <SlideNumber current={currentSlide + 1} total={slides.length} />
        <div className="ctrl-group">
          <button
            className="ctrl-btn download-btn"
            onClick={handleDownload}
            disabled={downloading}
            data-testid="download-btn"
            title="Download PPT"
          >
            <Download size={18} />
            {downloading ? 'Generating...' : 'Download PPT'}
          </button>
          <button className="ctrl-btn" onClick={toggleFullscreen} data-testid="fullscreen-btn" title="Fullscreen">
            {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
          </button>
        </div>
      </div>

      {/* Slide Area */}
      <div className="slide-area" data-testid="slide-area">
        <div className="slide-frame" data-testid="slide-frame">
          {renderSlide()}
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="bottom-nav" data-testid="bottom-nav">
        <button className="nav-btn" onClick={goPrev} disabled={currentSlide === 0} data-testid="prev-btn">
          <ChevronLeft size={20} /> Previous
        </button>
        <div className="progress-bar" data-testid="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <button className="nav-btn" onClick={goNext} disabled={currentSlide === slides.length - 1} data-testid="next-btn">
          Next <ChevronRight size={20} />
        </button>
      </div>

      {/* Sidebar */}
      {showSidebar && (
        <Sidebar
          slides={slides}
          current={currentSlide}
          onSelect={setCurrentSlide}
          onClose={() => setShowSidebar(false)}
          modules={moduleInfo}
        />
      )}
    </div>
  );
}

export default App;
